/**
 * Invite acceptance (QM-A12-001, QM-D05-002): /invite#token=... (canonical) and the legacy /join?token= and
 * /accept-invite?token= links. POST /v1/team/invites/accept (public); a signed-in caller's own email always wins.
 * The token is read from the fragment and removed from the address bar before anything else happens.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bffJson } from '../../../lib/bff';
import { errorText } from './TeamPage';

function readToken(): string | null {
  if (typeof window === 'undefined') return null;
  const frag = new URLSearchParams(window.location.hash.slice(1)).get('token');
  const query = new URLSearchParams(window.location.search).get('token');
  return frag ?? query;
}

export function InviteAcceptPage() {
  const { t, me } = useApp();
  const [token] = useState(readToken);
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (typeof window !== 'undefined' && token) window.history.replaceState(null, '', window.location.pathname); }, [token]);
  const accept = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!token) return;
    setState('busy');
    setError(null);
    try {
      await bffJson('/v1/team/invites/accept', 'POST', me ? { token } : { token, email });
      setState('done');
    } catch (err) { setError(errorText(t, err)); setState('idle'); }
  };
  return (
    <section className="qm-page" aria-labelledby="page-title" data-testid="invite-accept">
      <Panel>
        <Heading level={1} id="page-title">{t('shell.invite.title')}</Heading>
        {!token ? <Text role="alert">{t('shell.invite.missing')}</Text> : state === 'done' ? (
          <><Text role="status">{t('shell.invite.accepted')}</Text><Link to={me ? '/' : '/signin'}>{me ? t('shell.notFound.home') : t('shell.invite.signIn')}</Link></>
        ) : (
          <form onSubmit={accept} className="qm-form">
            <Text tone="muted">{t('shell.invite.intro')}</Text>
            {!me ? <label className="qm-field"><span className="qm-field__label">{t('shell.team.email')}</span>
              <input className="qm-input" type="email" autoComplete="email" required value={email} onChange={(ev) => setEmail(ev.target.value)} /></label> : null}
            <Button type="submit" variant="primary" disabled={state === 'busy' || (!me && !email)}>{t('shell.invite.accept')}</Button>
            {error ? <Text role="alert">{error}</Text> : null}
          </form>
        )}
      </Panel>
    </section>
  );
}
